import { useCallback, useMemo, useState } from "react";
import { BrowserNotificationToggle } from "../../components/BrowserNotificationToggle";
import { PushNotificationToggle } from "../../components/PushNotificationToggle";
import { useBrowserNotifications } from "../../hooks/useBrowserNotifications";
import { useConnectedDevices } from "../../hooks/useConnectedDevices";
import {
  type NotificationSettings,
  useNotificationSettings,
} from "../../hooks/useNotificationSettings";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import {
  type PushDeliveryUrgency,
  type SubscribedDevice,
  type TestNotificationUrgency,
  useSubscribedDevices,
} from "../../hooks/useSubscribedDevices";
import { useI18n } from "../../i18n";
import { useSettingsPaneTitle } from "./SettingsPaneTitleContext";
import { useSettingsUndoBaseline } from "./SettingsUndoContext";

/**
 * Unified device that merges subscribed device info with connection status.
 */
interface UnifiedDevice {
  browserProfileId: string;
  /** Device name from push subscription, or truncated UUID */
  displayName: string;
  /** Browser type suffix (e.g., "(Android/Chrome)") */
  browserType: string;
  /** True if device has push subscription */
  isSubscribed: boolean;
  /** Coarse device type inferred by the server */
  deviceType: SubscribedDevice["deviceType"];
  /** True if device is currently connected */
  isConnected: boolean;
  /** Number of connected tabs (0 if not connected) */
  tabCount: number;
  /** Subscription date (if subscribed) */
  subscribedAt?: string;
  /** True if this is the current device */
  isCurrentDevice: boolean;
}

/**
 * Format a device name with its domain for display.
 * Returns the display name and browser type separately.
 */
function formatDeviceName(
  deviceName: string | undefined,
  endpointDomain: string | undefined,
): { displayName: string; browserType: string } {
  const name = deviceName || "Unknown device";

  // Extract push service type from domain
  if (endpointDomain?.includes("google")) {
    return { displayName: name, browserType: "(Android/Chrome)" };
  }
  if (
    endpointDomain?.includes("apple") ||
    endpointDomain?.includes("push.apple")
  ) {
    return { displayName: name, browserType: "(iOS/Safari)" };
  }
  if (
    endpointDomain?.includes("mozilla") ||
    endpointDomain?.includes("push.services.mozilla")
  ) {
    return { displayName: name, browserType: "(Firefox)" };
  }
  return { displayName: name, browserType: "" };
}

/**
 * Format a date string to a relative or absolute format.
 */
function formatDate(
  dateString: string,
  t: (key: never, vars?: Record<string, string | number>) => string,
): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return new Date().toLocaleDateString();
  }
  if (diffDays === 1) {
    return new Date(Date.now() - 86400000).toLocaleDateString();
  }
  if (diffDays < 7) {
    return t("hostPickerLastConnectedDays" as never, { count: diffDays });
  }
  return date.toLocaleDateString();
}

/**
 * Merge subscribed devices with connected devices into a unified list.
 * Sorts: current device first, then connected devices, then offline subscribed.
 */
function mergeDevices(
  subscribedDevices: SubscribedDevice[],
  connectedDevices: Map<
    string,
    { connectionCount: number; deviceName?: string }
  >,
  currentBrowserProfileId: string | null,
): UnifiedDevice[] {
  const deviceMap = new Map<string, UnifiedDevice>();

  // Add subscribed devices first
  for (const device of subscribedDevices) {
    const { displayName, browserType } = formatDeviceName(
      device.deviceName,
      device.endpointDomain,
    );
    const connection = connectedDevices.get(device.browserProfileId);

    deviceMap.set(device.browserProfileId, {
      browserProfileId: device.browserProfileId,
      displayName,
      browserType,
      isSubscribed: true,
      deviceType: device.deviceType,
      isConnected: !!connection,
      tabCount: connection?.connectionCount ?? 0,
      subscribedAt: device.createdAt,
      isCurrentDevice: device.browserProfileId === currentBrowserProfileId,
    });
  }

  // Add connected-but-not-subscribed devices
  for (const [browserProfileId, connection] of connectedDevices) {
    if (!deviceMap.has(browserProfileId)) {
      // Not subscribed, show truncated UUID
      const truncatedId = browserProfileId.slice(0, 8);
      deviceMap.set(browserProfileId, {
        browserProfileId,
        displayName: truncatedId,
        browserType: "",
        isSubscribed: false,
        deviceType: "unknown",
        isConnected: true,
        tabCount: connection.connectionCount,
        isCurrentDevice: browserProfileId === currentBrowserProfileId,
      });
    }
  }

  // Convert to array and sort
  const devices = Array.from(deviceMap.values());

  devices.sort((a, b) => {
    // Current device first
    if (a.isCurrentDevice && !b.isCurrentDevice) return -1;
    if (!a.isCurrentDevice && b.isCurrentDevice) return 1;

    // Then connected devices (sorted by tab count descending)
    if (a.isConnected && !b.isConnected) return -1;
    if (!a.isConnected && b.isConnected) return 1;
    if (a.isConnected && b.isConnected) {
      return b.tabCount - a.tabCount;
    }

    // Then offline subscribed (sorted by subscription date, newest first)
    if (a.subscribedAt && b.subscribedAt) {
      return (
        new Date(b.subscribedAt).getTime() - new Date(a.subscribedAt).getTime()
      );
    }

    return 0;
  });

  return devices;
}

function isMobilePushDevice(device: UnifiedDevice): boolean {
  return (
    device.isSubscribed &&
    (device.deviceType === "android" ||
      device.deviceType === "ios" ||
      device.deviceType === "mobile")
  );
}

/**
 * The server-side notification-type toggles, gated on having at least one
 * push-subscribed device (without one, no notification could be delivered).
 */
const SERVER_NOTIFICATION_ROWS: ReadonlyArray<{
  key: keyof NotificationSettings;
  titleKey:
    | "notificationsToolApprovalsTitle"
    | "notificationsQuestionsTitle"
    | "notificationsSessionHaltedTitle"
    | "notificationsProjectInactiveTitle"
    | "notificationsYaInactiveTitle";
  descriptionKey:
    | "notificationsToolApprovalsDescription"
    | "notificationsQuestionsDescription"
    | "notificationsSessionHaltedDescription"
    | "notificationsProjectInactiveDescription"
    | "notificationsYaInactiveDescription";
  defaultValue: boolean;
}> = [
  {
    key: "toolApproval",
    titleKey: "notificationsToolApprovalsTitle",
    descriptionKey: "notificationsToolApprovalsDescription",
    defaultValue: true,
  },
  {
    key: "userQuestion",
    titleKey: "notificationsQuestionsTitle",
    descriptionKey: "notificationsQuestionsDescription",
    defaultValue: true,
  },
  {
    key: "sessionHalted",
    titleKey: "notificationsSessionHaltedTitle",
    descriptionKey: "notificationsSessionHaltedDescription",
    defaultValue: false,
  },
  {
    key: "projectInactive",
    titleKey: "notificationsProjectInactiveTitle",
    descriptionKey: "notificationsProjectInactiveDescription",
    defaultValue: false,
  },
  {
    key: "yaInactive",
    titleKey: "notificationsYaInactiveTitle",
    descriptionKey: "notificationsYaInactiveDescription",
    defaultValue: false,
  },
];

function deliveryPriorityLabelKey(
  urgency: PushDeliveryUrgency,
):
  | "pushDeliveryHigh"
  | "pushDeliveryNormal"
  | "pushDeliveryLow"
  | "pushDeliveryVeryLow" {
  if (urgency === "high") return "pushDeliveryHigh";
  if (urgency === "low") return "pushDeliveryLow";
  if (urgency === "very-low") return "pushDeliveryVeryLow";
  return "pushDeliveryNormal";
}

export function NotificationsSettings() {
  const { t } = useI18n();
  useSettingsPaneTitle(t("settingsNotificationsTitle"));
  const { browserProfileId } = usePushNotifications();
  const { isMobile } = useBrowserNotifications();
  const {
    devices: subscribedDevices,
    isLoading: devicesLoading,
    removeDevice,
    sendTest,
  } = useSubscribedDevices();
  const { connections, isLoading: connectionsLoading } = useConnectedDevices();
  const {
    settings,
    isLoading: settingsLoading,
    updateSetting,
  } = useNotificationSettings();

  const hasSubscriptions = subscribedDevices.length > 0;
  const isLoading = devicesLoading || connectionsLoading;
  const [testDisplayUrgency, setTestDisplayUrgency] =
    useState<TestNotificationUrgency>("normal");
  const [testDeliveryUrgency, setTestDeliveryUrgency] =
    useState<PushDeliveryUrgency>("high");
  const [testingDeviceIds, setTestingDeviceIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [testStatus, setTestStatus] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);

  // Header undo for the server-side notification toggles. Push/browser
  // subscription state is device-permission-bound and not snapshot-undoable.
  const undoState = useMemo(
    () =>
      settings
        ? {
            toolApproval: settings.toolApproval,
            userQuestion: settings.userQuestion,
            sessionHalted: settings.sessionHalted,
            projectInactive: settings.projectInactive,
            yaInactive: settings.yaInactive,
          }
        : null,
    [settings],
  );
  const restoreUndoState = useCallback(
    (snapshot: NonNullable<typeof undoState>) => {
      void updateSetting("toolApproval", snapshot.toolApproval);
      void updateSetting("userQuestion", snapshot.userQuestion);
      void updateSetting("sessionHalted", snapshot.sessionHalted);
      void updateSetting("projectInactive", snapshot.projectInactive);
      void updateSetting("yaInactive", snapshot.yaInactive);
    },
    [updateSetting],
  );
  useSettingsUndoBaseline(undoState, restoreUndoState);

  // Merge subscribed and connected devices
  const unifiedDevices = mergeDevices(
    subscribedDevices,
    connections,
    browserProfileId,
  );
  const mobilePushDevices = useMemo(
    () => unifiedDevices.filter(isMobilePushDevice),
    [unifiedDevices],
  );
  const isTestingMobileDevices = mobilePushDevices.some((device) =>
    testingDeviceIds.has(device.browserProfileId),
  );

  const buildTestMessage = useCallback(
    () =>
      t("pushTestMessage", {
        priority: t(deliveryPriorityLabelKey(testDeliveryUrgency)),
      }),
    [t, testDeliveryUrgency],
  );

  const markTesting = useCallback(
    (browserProfileIds: string[], testing: boolean) => {
      setTestingDeviceIds((current) => {
        const next = new Set(current);
        for (const browserProfileId of browserProfileIds) {
          if (testing) {
            next.add(browserProfileId);
          } else {
            next.delete(browserProfileId);
          }
        }
        return next;
      });
    },
    [],
  );

  const sendTestToDevice = useCallback(
    async (device: UnifiedDevice) => {
      markTesting([device.browserProfileId], true);
      setTestStatus(null);
      try {
        await sendTest(device.browserProfileId, {
          displayUrgency: testDisplayUrgency,
          deliveryUrgency: testDeliveryUrgency,
          message: buildTestMessage(),
        });
        setTestStatus({
          kind: "success",
          message: t("notificationsTestSentToDevice", {
            device: device.displayName,
          }),
        });
      } catch (err) {
        setTestStatus({
          kind: "error",
          message:
            err instanceof Error ? err.message : t("notificationsTestFailed"),
        });
      } finally {
        markTesting([device.browserProfileId], false);
      }
    },
    [
      buildTestMessage,
      markTesting,
      sendTest,
      t,
      testDeliveryUrgency,
      testDisplayUrgency,
    ],
  );

  const sendTestToMobileDevices = useCallback(async () => {
    const targetIds = mobilePushDevices.map(
      (device) => device.browserProfileId,
    );
    if (targetIds.length === 0) return;

    markTesting(targetIds, true);
    setTestStatus(null);
    try {
      const results = await Promise.allSettled(
        mobilePushDevices.map((device) =>
          sendTest(device.browserProfileId, {
            displayUrgency: testDisplayUrgency,
            deliveryUrgency: testDeliveryUrgency,
            message: buildTestMessage(),
          }),
        ),
      );
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length > 0) {
        setTestStatus({
          kind: "error",
          message: t("notificationsTestPartialFailure", {
            failed: failed.length,
            total: results.length,
          }),
        });
      } else {
        setTestStatus({
          kind: "success",
          message: t("notificationsTestSentToMobile", {
            count: results.length,
          }),
        });
      }
    } finally {
      markTesting(targetIds, false);
    }
  }, [
    buildTestMessage,
    markTesting,
    mobilePushDevices,
    sendTest,
    t,
    testDeliveryUrgency,
    testDisplayUrgency,
  ]);

  const serverTogglesGated = !hasSubscriptions;
  const gatedTooltip = serverTogglesGated
    ? t("notificationsNoSubscribedDevices")
    : undefined;

  return (
    <>
      {/* Push notifications, with the server-side notification types they
          gate scoped underneath: without a subscribed device the server has
          nowhere to deliver, so the type toggles are disabled. */}
      <section className="settings-section">
        <h2>{t("notificationsPushTitle")}</h2>
        <p className="settings-section-description">
          {t("notificationsPushDescription")}
        </p>
        <div className="settings-group">
          <PushNotificationToggle />
        </div>
        <div className="settings-subsection">
          <h3>{t("notificationsServerTitle")}</h3>
          <p className="settings-section-description">
            {t("notificationsServerDescription")}
          </p>
          {serverTogglesGated && !devicesLoading && (
            <div className="settings-info-box settings-subsection-hint">
              <p>{t("notificationsNoSubscribedDevices")}</p>
            </div>
          )}
          <div className="settings-group">
            {SERVER_NOTIFICATION_ROWS.map((row) => (
              <div
                key={row.key}
                className="settings-item"
                title={gatedTooltip}
              >
                <div className="settings-item-info">
                  <strong>{t(row.titleKey)}</strong>
                  <p>{t(row.descriptionKey)}</p>
                </div>
                <label className="toggle-switch" title={gatedTooltip}>
                  <input
                    type="checkbox"
                    checked={settings?.[row.key] ?? row.defaultValue}
                    onChange={(e) => updateSetting(row.key, e.target.checked)}
                    disabled={settingsLoading || serverTogglesGated}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Desktop notifications - browser Notification API (not available on mobile) */}
      {!isMobile && (
        <section className="settings-section">
          <h2>{t("notificationsDesktopTitle")}</h2>
          <p className="settings-section-description">
            {t("notificationsDesktopDescription")}
          </p>
          <div className="settings-group">
            <BrowserNotificationToggle />
          </div>
        </section>
      )}

      {/* Unified devices list */}
      <section className="settings-section">
        <h2>{t("notificationsDevicesTitle")}</h2>
        <p className="settings-section-description">
          {t("notificationsDevicesDescription")}
        </p>
        <div className="settings-group">
          {hasSubscriptions && (
            <div className="settings-item push-test-controls">
              <div className="settings-item-info">
                <strong>{t("notificationsTestPushTitle")}</strong>
                <p>{t("notificationsTestPushDescription")}</p>
                <p
                  className={`push-test-status ${
                    testStatus?.kind === "error"
                      ? "settings-error"
                      : "settings-hint"
                  }`}
                  aria-live="polite"
                >
                  {testStatus?.message ?? "\u00a0"}
                </p>
              </div>
              <div className="settings-item-actions">
                <select
                  className="settings-select"
                  aria-label={t("pushToggleDisplayBehavior")}
                  value={testDisplayUrgency}
                  onChange={(e) =>
                    setTestDisplayUrgency(
                      e.target.value as TestNotificationUrgency,
                    )
                  }
                  disabled={isTestingMobileDevices}
                >
                  <option value="normal">{t("pushToggleUrgencyNormal")}</option>
                  <option value="persistent">
                    {t("pushToggleUrgencyPersistent")}
                  </option>
                  <option value="silent">{t("pushToggleUrgencySilent")}</option>
                </select>
                <select
                  className="settings-select"
                  aria-label={t("pushTestDeliveryPriority")}
                  value={testDeliveryUrgency}
                  onChange={(e) =>
                    setTestDeliveryUrgency(
                      e.target.value as PushDeliveryUrgency,
                    )
                  }
                  disabled={isTestingMobileDevices}
                >
                  <option value="high">{t("pushDeliveryHigh")}</option>
                  <option value="normal">{t("pushDeliveryNormal")}</option>
                  <option value="low">{t("pushDeliveryLow")}</option>
                  <option value="very-low">{t("pushDeliveryVeryLow")}</option>
                </select>
                <button
                  type="button"
                  className="settings-button"
                  onClick={sendTestToMobileDevices}
                  disabled={
                    mobilePushDevices.length === 0 || isTestingMobileDevices
                  }
                  title={
                    mobilePushDevices.length === 0
                      ? t("notificationsNoMobilePushDevices")
                      : t("notificationsSendToMobileDevices", {
                          count: mobilePushDevices.length,
                        })
                  }
                >
                  {t("notificationsSendToMobileDevices", {
                    count: mobilePushDevices.length,
                  })}
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <p className="settings-hint">{t("notificationsLoadingDevices")}</p>
          ) : unifiedDevices.length === 0 ? (
            <p className="settings-hint">{t("notificationsNoDevices")}</p>
          ) : (
            <div className="device-list">
              {unifiedDevices.map((device) => (
                <div key={device.browserProfileId} className="device-list-item">
                  <div className="device-list-info">
                    <strong>
                      {device.displayName}
                      {device.browserType && ` ${device.browserType}`}
                      {device.isCurrentDevice && (
                        <span className="device-current-badge">
                          {t("notificationsThisDevice")}
                        </span>
                      )}
                    </strong>
                    <p>
                      {/* Status indicator */}
                      {device.isConnected ? (
                        <span className="device-status device-status-online">
                          {device.tabCount === 1
                            ? t("notificationsOneTab")
                            : t("notificationsTabs", {
                                count: device.tabCount,
                              })}
                        </span>
                      ) : (
                        <span className="device-status device-status-offline">
                          {t("notificationsOffline")}
                        </span>
                      )}
                      {/* No push indicator for connected-only devices */}
                      {!device.isSubscribed && (
                        <span className="device-no-push">
                          {t("notificationsNoPush")}
                        </span>
                      )}
                      {/* Subscription date for subscribed devices */}
                      {device.subscribedAt && (
                        <span className="device-subscribed-date">
                          {t("notificationsSubscribed", {
                            date: formatDate(device.subscribedAt, t),
                          })}
                        </span>
                      )}
                    </p>
                  </div>
                  {/* Only show remove button for subscribed devices */}
                  {device.isSubscribed && (
                    <div className="device-list-actions">
                      <button
                        type="button"
                        className="settings-button"
                        onClick={() => sendTestToDevice(device)}
                        disabled={testingDeviceIds.has(device.browserProfileId)}
                        title={t("notificationsTestDevice")}
                      >
                        {t("notificationsTest")}
                      </button>
                      <button
                        type="button"
                        className="settings-button settings-button-danger-subtle"
                        onClick={() => removeDevice(device.browserProfileId)}
                        title={
                          device.isCurrentDevice
                            ? t("notificationsRemoveThisDevice")
                            : t("notificationsRemoveDevice")
                        }
                      >
                        {t("notificationsRemove")}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
